/**
 * @file A Game's Options controller
 * @author Gonzalo Alvarez de Aldecoa <gon@galdecoa.com>
 * @license GPL-3.0-or-later
 * @version 2310.01.001
 */


/** @module gameOptions */

/**
 * The game's default settings.
 * `speed` controls the global speed of the game.
 * `showGrid` indicates wether to draw the Tiled grid or not.
 * `showCollisions` enables showing the collision boxes.
 * `playerSpeed` represents the proportion of the character's movement speed relative to other elements.
 * @type {Object}
 * @default { speed: 2.5, showGrid: false, showCollisions: false, playerSpeed: 1.75 }
 */
const defaultSettings = Object.freeze( {
	speed: 2.5,
	showGrid: false,
	showCollisions: false,
	playerSpeed: 1.75
} );

/**
 * The game's stored settings.
 * @type {Object}
 * @default { speed: 2.5, showGrid: false, showCollisions: false, playerSpeed: 1.75 }
 */
const settings = ( typeof localStorage == "object" )
	? Object.assign( 
		{},
		defaultSettings,
		JSON.parse( localStorage.getItem( "settings" ) )
	)
	: defaultSettings
;

/**
 * Save the user's settings to the `localStorage`.
 */
function saveSettings() {
	const differences = Object.assign( {}, settings );
	for( let setting in defaultSettings ) {
		if ( 
			   differences.hasOwnProperty( setting ) 
			&& ( differences[ setting ] === defaultSettings[ setting ] )
		) {
			delete differences[ setting ];
		}
	}
	localStorage.setItem( "settings", JSON.stringify( differences ) );
}

/**
 * Descriptors and handlers of the game setting's options' elements.
 * Each descriptor has a CSS `selector`, an `updateGame` function that is called
 * when the control is initialised, and an `eventName` and `eventHandler` that 
 * describe an event type and its corresponding event handler. 
 * `updateGame` and `eventHandler` require a `Game` to be passed as first
 * argument.
 * @type {Object}
 */
const gameOptionsMeta = {
	speed: { 
		selector: "#game-movement-speed", 
		updateGame: function( game, control ) { 
			game.setMovementSpeed( control.value );
		},
		eventName: "change",
		eventHandler: function( game, changeEvent ) {
			settings.speed = changeEvent.target.value * 1;
			game.setMovementSpeed( settings.speed );
			saveSettings();
		} 
	},
	showGrid: {
		selector: "#show-grid",
		updateGame: function( game, control ) { game.showGrid( control.checked ); },
		eventName: "click",
		eventHandler: function( game, clickEvent ) {
			settings.showGrid = clickEvent.target.checked;
			game.showGrid( settings.showGrid );
			saveSettings();
		}
	},
	showCollisions: {
		selector: "#show-collisions",
		updateGame: function( game, control ) { 
			game.showCollisions( control.checked ); 
		},
		eventName: "click",
		eventHandler: function( game, clickEvent ) {
			settings.showCollisions = clickEvent.target.checked;
			game.showCollisions( settings.showCollisions );
			saveSettings();
		}
	},
	playerSpeed: { 
		selector: "#player-speed-factor", 
		updateGame: function( game, control ) { 
			game.setPlayerSpeedFactor( control.value ); 
		},
		eventName: "change",
		eventHandler: function( game, changeEvent ) {
			settings.playerSpeed = changeEvent.target.value * 1;
			game.setPlayerSpeedFactor( settings.playerSpeed );
			saveSettings();
		} 
	},
	pause: {
		selector: "#pause",
		eventName: "click",
		eventHandler: function( game, clickEvent ) { 
			game.pause( clickEvent.target.checked ); 
		}
	}
};

/**
 * Sets up the controls for the game options.
 * @param {module:game~Game} game 
 * @param {HTMLElement} gameOptions 
 */
export function initialiseOptionsControls( game, gameOptions ) {

	for( let gameOptionName in gameOptionsMeta ) {

		const gameOptionMeta = gameOptionsMeta[ gameOptionName ];
		const gameOption = gameOptions.querySelector( gameOptionMeta.selector );

		// Match control state with the stored values
		if ( settings.hasOwnProperty( gameOptionName ) ) {
			const gameOptionProperty = ( gameOption.type === "checkbox" )
				? "checked"
				: "value"
			;
			gameOption[ gameOptionProperty ] = settings[ gameOptionName ];
		}

		// Update the game state to match the control state
		if ( 
			   gameOptionMeta.hasOwnProperty( "updateGame" ) 
			&& ( typeof gameOptionMeta.updateGame === "function" )
		) {
			gameOptionMeta.updateGame( game, gameOption );
		}

		// Define and bind the event handler that has access to the `Game` instance
		const onEvent = function( event ) {
			gameOptionMeta.eventHandler( game, event );
		}
		gameOption.addEventListener( gameOptionMeta.eventName, onEvent );

	}

}